import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../fundo_liquido/style.css';
import './style.css';
import { TextField, TextAreaField, SelectField, DateField } from '../fundo_liquido/fields';
import { Opt } from '../fundo_liquido/mock';
import { useToast } from '../../../components/toast_notification/ToastProvider';
import BotaoVoltar from '../../../components/BotaoVoltar/BotaoVoltar';
import EmissorField from './EmissorField';
import CamposDinamicos from './CamposDinamicos';
import { AtivoForm, ativoVazio, VinculoFundo } from './types';
import { TIPOS_ATIVO, getTipoDef, campoVisivel, dadosIniciais } from './camposConfig';
import { cadastrarAtivo, listarPrestadorOpts, listarFundoOpts } from './ativosService';

let _seq = 0;
const novaKey = () => `k${_seq++}`;

const STATUS_OPTS: Opt[] = [{ value: 'ativo', label: 'Ativo' }, { value: 'inativo', label: 'Inativo' }];
const primeiroTipo = () => Object.keys(TIPOS_ATIVO)[0];
const formInicial = (tipo: string): AtivoForm => {
  const def = getTipoDef(tipo);
  return { ...ativoVazio(tipo), dados: def ? dadosIniciais(def) : {} };
};

const CadastroAtivo: React.FC<{ onVoltar?: () => void; onCadastrado?: (ativoId: number) => void }> = ({ onVoltar, onCadastrado }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { tipo: tipoParam } = useParams<{ tipo?: string }>();
  const tipoInicial = tipoParam && getTipoDef(tipoParam) ? tipoParam : primeiroTipo();

  const [form, setForm] = useState<AtivoForm>(() => formInicial(tipoInicial));
  const [erros, setErros] = useState<Record<string, string>>({});
  const [tentou, setTentou] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [prestadorOpts, setPrestadorOpts] = useState<Opt[]>([]);
  const [fundoOpts, setFundoOpts] = useState<Opt[]>([]);

  useEffect(() => {
    listarPrestadorOpts().then(setPrestadorOpts).catch(() => setPrestadorOpts([]));
    listarFundoOpts().then(setFundoOpts).catch(() => setFundoOpts([]));
  }, []);

  useEffect(() => {
    if (tipoParam && getTipoDef(tipoParam)) { setForm(formInicial(tipoParam)); setTentou(false); setErros({}); }
  }, [tipoParam]);

  const def = useMemo(() => getTipoDef(form.tipo_ativo), [form.tipo_ativo]);
  const voltarTipos = () => (onVoltar ? onVoltar() : navigate('/cadastro/ativo'));

  const set = (campo: keyof AtivoForm, v: any) => setForm((p) => ({ ...p, [campo]: v }));
  const setDado = (nome: string, v: any) => setForm((p) => ({ ...p, dados: { ...p.dados, [nome]: v } }));

  const addFundo = () => setForm((p) => ({ ...p, fundos: [...p.fundos, { _key: novaKey() }] }));
  const setFundo = (key: string, patch: Partial<VinculoFundo>) =>
    setForm((p) => ({ ...p, fundos: p.fundos.map((f) => (f._key === key ? { ...f, ...patch } : f)) }));
  const rmFundo = (key: string) =>
    setForm((p) => ({ ...p, fundos: p.fundos.filter((f) => f._key !== key) }));

  const validar = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.codigo_interno.trim()) e.codigo_interno = 'Informe o código interno.';
    if (!form.nome_curto.trim()) e.nome_curto = 'Informe o nome curto / Phibra.';
    const emissorObrig = !def?.semEmissor && def?.emissorObrigatorio !== false;
    if (emissorObrig && !form.emissor_id) e.emissor_id = `Selecione ${(def?.emissorLabel ?? 'o emissor').toLowerCase()}.`;
    for (const s of def?.secoes ?? []) {
      for (const c of s.campos) {
        if (c.obrigatorio && c.tipo !== 'checkbox' && campoVisivel(c, form.dados)) {
          const v = form.dados[c.nome];
          if (v === undefined || v === null || String(v).trim() === '') e[c.nome] = `Campo obrigatório.`;
        }
      }
    }
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const salvar = async () => {
    if (salvando) return;
    setTentou(true);
    if (!validar()) {
      toast.warning('Preencha os campos obrigatórios destacados.');
      return;
    }
    setSalvando(true);
    try {
      const { ativoId } = await cadastrarAtivo(form);
      toast.success(`Ativo "${form.nome_curto}" cadastrado (#${ativoId}).`);
      if (onCadastrado) onCadastrado(ativoId);
      else { setForm(formInicial(form.tipo_ativo)); setTentou(false); setErros({}); }
    } catch (err: any) {
      const det = err?.details ? ` — ${JSON.stringify(err.details)}` : '';
      toast.error(`Falha ao cadastrar: ${err?.message ?? 'erro'}${det}`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="av-page">
      <header className="av-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BotaoVoltar onClick={voltarTipos} />
            <h1 className="av-title" style={{ margin: 0 }}>
              {def ? `Novo ativo · ${def.label}` : 'Cadastro de ativo'}
            </h1>
          </div>
          <p className="av-sub">
            {def ? `${def.grupoLabel} — preencha os dados e vincule aos fundos que o detêm.`
                 : 'Tipo de ativo não configurado.'}
          </p>
        </div>
      </header>

      {!def && (
        <section className="av-card">
          <p className="av-sub">Este tipo de ativo ainda não foi configurado no cadastro.</p>
          <button type="button" className="fl-btn fl-btn--primary" style={{ marginTop: 12 }} onClick={voltarTipos}>
            Ver tipos disponíveis
          </button>
        </section>
      )}

      {def && (<>

      {}
      <section className="av-card">
        <h3 className="av-card-title">Identificação</h3>
        <div className="fl-grid">
          <TextField
            label="Código interno" required value={form.codigo_interno}
            onChange={(v) => set('codigo_interno', v)}
            error={tentou ? erros.codigo_interno : undefined}
            hint="Identificador único na plataforma Angá"
          />
          <TextField
            label="Nome curto / Phibra" required value={form.nome_curto}
            onChange={(v) => set('nome_curto', v)}
            error={tentou ? erros.nome_curto : undefined}
          />
          {!def?.semEmissor && (
            <EmissorField
              label={def?.emissorLabel ?? 'Emissor'}
              required={def?.emissorObrigatorio !== false}
              value={form.emissor_id}
              onChange={(id, nome) => setForm((p) => ({ ...p, emissor_id: id, emissor_nome: nome }))}
            />
          )}
          <SelectField
            label="Status"
            value={form.is_active ? 'ativo' : 'inativo'}
            onChange={(v) => set('is_active', v === 'ativo')}
            options={STATUS_OPTS}
          />
          <TextAreaField
            label="Observações" full value={form.observacao ?? ''}
            onChange={(v) => set('observacao', v)}
          />
        </div>
        {tentou && erros.emissor_id && <div className="av-erro">{erros.emissor_id}</div>}
      </section>

      {}
      <CamposDinamicos
        secoes={def.secoes}
        dados={form.dados}
        prestadorOpts={prestadorOpts}
        fundoOpts={fundoOpts}
        erros={tentou ? erros : {}}
        onChange={setDado}
      />

      {}
      <section className="av-card">
        <div className="av-card-head">
          <h3 className="av-card-title">Fundos (carteira)</h3>
          <button type="button" className="fl-btn fl-btn--ghost" onClick={addFundo}>＋ Vincular fundo</button>
        </div>
        {form.fundos.length === 0 ? (
          <p className="av-sub">Nenhum fundo vinculado. Um ativo pode pertencer a vários fundos.</p>
        ) : (
          <div className="av-fundos">
            {form.fundos.map((f) => (
              <div className="av-fundo-row" key={f._key}>
                <SelectField
                  label="Fundo" value={f.fundo_id ?? ''}
                  onChange={(v) => setFundo(f._key, { fundo_id: v })}
                  options={fundoOpts} placeholder="Selecione o fundo..."
                />
                <DateField label="Entrada" value={f.data_inicio ?? ''} onChange={(v) => setFundo(f._key, { data_inicio: v })} />
                <DateField label="Saída" value={f.data_fim ?? ''} onChange={(v) => setFundo(f._key, { data_fim: v })} />
                <button type="button" className="av-rm" title="Remover" onClick={() => rmFundo(f._key)}>✕</button>
              </div>
            ))}
          </div>
        )}
      </section>

      {}
      <footer className="av-footer">
        {onVoltar && (
          <button type="button" className="fl-btn fl-btn--ghost" onClick={onVoltar} disabled={salvando}>Cancelar</button>
        )}
        <button type="button" className="fl-btn fl-btn--primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Cadastrar ativo'}
        </button>
      </footer>
      </>)}
    </div>
  );
};

export default CadastroAtivo;
